// Variant B — hover hotspots + edge card.
//
// The designer's recommended default surface for the public viewer
// (`/tmp/designer-wf/quidities/project/viewer-variants.jsx:140-227`). Faint
// dots on every part; hover reveals a small tooltip; click promotes a part to
// active state (orange-glow ring) and pops a detail card in the bottom-right
// with sku / price / stock / qty stepper / Add-to-cart. By default a
// persistent cart pill in the top-right also tracks added items.
//
// All cart/checkout actions are emitted through the event bus — the viewer
// owns no cart state beyond the pill's running tally. Host wires `cart:add`
// to whatever downstream behaviour fits (Shopify, Odoo, local stub, …).
//
// When mounted inside a real storefront (Shopify / Odoo / the quidities.com
// marketing hero), the host already has its own cart UI; rendering ours inside
// the embed makes the widget look like a self-contained mini-storefront.
// `showCart: false` suppresses the pill + internal tally so the embed is a
// pure part-picker that just emits `cart:add` events for the host to consume.

import type { AnchorFrame, ViewerController } from '../viewer-controller.js';
import type { CartLineItem, ViewerBus } from '../events.js';

export interface PartData {
  id: string;
  /** Short numeric/alphanumeric badge — defaults to the part's BOM row index
   *  formatted as `01`, `02`, … if the host doesn't supply one. */
  n: string;
  name: string;
  sku: string;
  price: number;
  currency: string;
  /** null = unknown (e.g. ERP unavailable); 0 = out of stock; > 0 = in stock. */
  stock: number | null;
}

export interface MountVariantBOptions {
  container: HTMLElement;
  viewer: ViewerController;
  bus: ViewerBus;
  /** Returns part-level metadata for an anchored node id, or `null` when no
   *  BOM/ERP data is known for it. The variant still renders an anchor dot
   *  for unknown parts so the user can see they exist — hover/click just
   *  shows a degraded card. */
  getPartData(id: string): PartData | null;
  /** Render the cart pill + Checkout button inside the overlay (default true).
   *  Set false when embedding inside a host that owns its own cart UI — the
   *  edge card still emits `cart:add` events for the host to consume. */
  showCart?: boolean;
}

export interface MountedVariantB {
  /** Programmatic select — same effect as clicking a dot. */
  setActive(id: string | null): void;
  /** Read the current cart contents (for tests / debugging). */
  getCart(): CartLineItem[];
  dispose(): void;
}

const ACCENT = '#FF6F00';
const ACCENT_SOFT = 'rgba(255, 111, 0, 0.18)';
const INK = '#18181B';
const INK_2 = '#3F3F46';
const INK_3 = '#71717A';
const LINE = '#E4E4E7';
const STOCK_IN = '#2C8A4F';
const STOCK_OUT = '#A04444';
const STOCK_UNK = '#9CA3AF';

export function mountVariantB(opts: MountVariantBOptions): MountedVariantB {
  const { container, viewer, bus, getPartData } = opts;
  const showCart = opts.showCart ?? true;

  const layer = document.createElement('div');
  layer.className = 'idiagram-variant-b';
  Object.assign(layer.style, {
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',
    zIndex: '5',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    color: INK,
  } satisfies Partial<CSSStyleDeclaration>);
  container.appendChild(layer);

  // ── Dot layer ──────────────────────────────────────────────────────────
  interface DotState {
    el: HTMLDivElement;
    lastX: number;
    lastY: number;
    lastVisible: boolean;
  }
  const dotByNodeId = new Map<string, DotState>();
  let hoveredId: string | null = null;
  let activeId: string | null = null;
  const cart: CartLineItem[] = [];

  // ── Tooltip ────────────────────────────────────────────────────────────
  const tooltip = document.createElement('div');
  Object.assign(tooltip.style, {
    position: 'absolute',
    background: INK,
    color: '#fff',
    padding: '4px 9px',
    borderRadius: '5px',
    fontSize: '11.5px',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    boxShadow: '0 4px 10px rgba(0,0,0,0.25)',
    transform: 'translate3d(0px, 0px, 0)',
    opacity: '0',
    transition: 'opacity 80ms linear',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    zIndex: '6',
  } satisfies Partial<CSSStyleDeclaration>);
  tooltip.className = 'idiagram-variant-b__tooltip';
  layer.appendChild(tooltip);

  // ── Cart pill ──────────────────────────────────────────────────────────
  // Only built when `showCart` is true. Hosts that own their own cart UI
  // (the quidities.com hero, real Shopify/Odoo storefronts) pass
  // `showCart: false` and listen to `cart:add` instead.
  let cartPill: HTMLDivElement | null = null;
  let cartSummary: HTMLElement | null = null;
  let checkoutBtn: HTMLButtonElement | null = null;
  if (showCart) {
    cartPill = document.createElement('div');
    cartPill.className = 'idiagram-variant-b__cart';
    Object.assign(cartPill.style, {
      position: 'absolute',
      top: '14px',
      right: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '8px 10px 8px 12px',
      background: 'rgba(255, 255, 255, 0.97)',
      border: `1px solid ${LINE}`,
      borderRadius: '999px',
      boxShadow: '0 6px 20px rgba(0,0,0,0.10)',
      fontSize: '12.5px',
      pointerEvents: 'auto',
      zIndex: '20',
    } satisfies Partial<CSSStyleDeclaration>);
    cartPill.innerHTML = `
      <span style="font-family:'Geist Mono',ui-monospace,monospace;font-size:10.5px;letter-spacing:.06em;color:${INK_3};">CART</span>
      <span class="idiagram-variant-b__cart-summary" style="color:${INK_2};">empty</span>
      <button class="idiagram-variant-b__checkout" type="button"
        style="border:0;background:${ACCENT};color:#fff;font-weight:600;font-size:12px;padding:6px 12px;border-radius:999px;cursor:pointer;font-family:inherit;opacity:0.45;pointer-events:none;">Checkout →</button>
    `;
    layer.appendChild(cartPill);
    cartSummary = cartPill.querySelector<HTMLElement>('.idiagram-variant-b__cart-summary')!;
    checkoutBtn = cartPill.querySelector<HTMLButtonElement>('.idiagram-variant-b__checkout')!;
    checkoutBtn.addEventListener('click', () => {
      if (cart.length === 0) return;
      bus.emit('checkout:request', {
        lineCount: cart.length,
        total: cart.reduce((s, l) => s + l.unitPrice * l.qty, 0),
        currency: cart[0]?.currency ?? '',
      });
    });
  }

  // ── Edge card (bottom-right when a part is active) ─────────────────────
  const edgeCard = document.createElement('div');
  edgeCard.className = 'idiagram-variant-b__edge-card';
  Object.assign(edgeCard.style, {
    position: 'absolute',
    right: '14px',
    bottom: '14px',
    width: '280px',
    background: '#fff',
    border: `1px solid ${LINE}`,
    borderRadius: '10px',
    padding: '14px',
    boxShadow: '0 6px 20px rgba(0,0,0,0.10)',
    pointerEvents: 'auto',
    zIndex: '15',
    display: 'none',
    transform: 'translateY(0)',
    opacity: '0',
    transition: 'opacity 120ms ease-out, transform 120ms ease-out',
  } satisfies Partial<CSSStyleDeclaration>);
  layer.appendChild(edgeCard);

  // ── Anchor stream → dots + active-dot + tooltip follow ─────────────────
  const unsubscribeAnchors = viewer.onAnchors((frame: AnchorFrame) => {
    const seen = new Set<string>();
    for (const a of frame.anchors) {
      seen.add(a.id);
      let dot = dotByNodeId.get(a.id);
      if (!dot) {
        const el = makeDot(a.id);
        layer.appendChild(el);
        wireDotInteraction(el, a.id);
        dot = { el, lastX: NaN, lastY: NaN, lastVisible: false };
        dotByNodeId.set(a.id, dot);
        // Apply current active styling to fresh dots if needed.
        if (a.id === activeId) styleDotActive(el, true);
      }
      if (a.visible !== dot.lastVisible) {
        dot.el.style.opacity = a.visible ? '1' : '0';
        dot.el.style.pointerEvents = a.visible ? 'auto' : 'none';
        dot.lastVisible = a.visible;
      }
      if (a.visible && (a.x !== dot.lastX || a.y !== dot.lastY)) {
        dot.el.style.transform = `translate3d(${a.x}px, ${a.y}px, 0)`;
        dot.lastX = a.x;
        dot.lastY = a.y;
      }
      // Move the tooltip with its part if currently hovered.
      if (a.id === hoveredId && a.visible) {
        tooltip.style.transform = `translate3d(${a.x + 14}px, ${a.y - 14}px, 0)`;
      }
    }
    // Drop any dots whose anchor disappeared (e.g. new assembly loaded).
    for (const [id, dot] of dotByNodeId) {
      if (!seen.has(id)) {
        dot.el.remove();
        dotByNodeId.delete(id);
        if (id === hoveredId) hideTooltip();
        if (id === activeId) setActive(null);
      }
    }
  });

  function makeDot(id: string): HTMLDivElement {
    const dot = document.createElement('div');
    dot.className = 'idiagram-variant-b__dot';
    dot.dataset.idiagramId = id;
    Object.assign(dot.style, {
      position: 'absolute',
      width: '10px',
      height: '10px',
      marginLeft: '-5px',
      marginTop: '-5px',
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.92)',
      border: '1.5px solid rgba(24, 24, 27, 0.75)',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.25)',
      cursor: 'pointer',
      opacity: '0',
      // Hit slop: a 16px transparent ring around the visible dot makes
      // hovering precise enough on real hardware without bloating the
      // visible shape.
      outline: '6px solid transparent',
      outlineOffset: '-1px',
      transition: 'background 120ms, box-shadow 120ms, transform 120ms',
    } satisfies Partial<CSSStyleDeclaration>);
    return dot;
  }

  function wireDotInteraction(el: HTMLDivElement, id: string): void {
    el.addEventListener('pointerenter', () => {
      hoveredId = id;
      showTooltipFor(id);
    });
    el.addEventListener('pointerleave', () => {
      if (hoveredId === id) {
        hoveredId = null;
        hideTooltip();
      }
    });
    el.addEventListener('click', (ev) => {
      ev.stopPropagation();
      setActive(id);
    });
  }

  // ── Tooltip ────────────────────────────────────────────────────────────
  function showTooltipFor(id: string): void {
    const data = getPartData(id);
    const dot = dotByNodeId.get(id);
    const numText = data?.n ?? '·';
    const nameText = data?.name ?? `node ${id}`;
    const priceText = data ? fmtPrice(data.price, data.currency) : '';
    tooltip.innerHTML = `
      <span style="font-family:'Geist Mono',ui-monospace,monospace;color:#A1A1AA;font-size:10px;">${esc(numText)}</span>
      <span>${esc(nameText)}</span>
      ${priceText ? `<span style="font-family:'Geist Mono',ui-monospace,monospace;color:#A1A1AA;font-size:10px;">${esc(priceText)}</span>` : ''}
    `;
    if (dot && dot.lastVisible) {
      tooltip.style.transform = `translate3d(${dot.lastX + 14}px, ${dot.lastY - 14}px, 0)`;
    }
    tooltip.style.opacity = '1';
    const anchor = anchorOf(id);
    if (anchor) bus.emit('part:hover', { id, anchor });
  }
  function hideTooltip(): void {
    tooltip.style.opacity = '0';
    bus.emit('part:hover', { id: null });
  }

  // ── Active state ───────────────────────────────────────────────────────
  function setActive(id: string | null): void {
    if (activeId === id) return;
    const prev = activeId;
    activeId = id;
    if (prev) {
      const prevDot = dotByNodeId.get(prev);
      if (prevDot) styleDotActive(prevDot.el, false);
    }
    if (id) {
      const dot = dotByNodeId.get(id);
      if (dot) styleDotActive(dot.el, true);
      renderEdgeCard(id);
      const anchor = anchorOf(id);
      if (anchor) bus.emit('part:select', { id, anchor, prev });
    } else {
      edgeCard.style.opacity = '0';
      edgeCard.style.transform = 'translateY(6px)';
      window.setTimeout(() => {
        if (!activeId) edgeCard.style.display = 'none';
      }, 150);
      if (prev) bus.emit('part:deselect', { prev });
    }
  }

  function styleDotActive(el: HTMLDivElement, on: boolean): void {
    if (on) {
      el.style.background = ACCENT;
      el.style.border = '3px solid #fff';
      el.style.boxShadow = `0 0 0 4px ${ACCENT_SOFT}, 0 0 0 1px ${ACCENT}`;
      el.style.width = '14px';
      el.style.height = '14px';
      el.style.marginLeft = '-7px';
      el.style.marginTop = '-7px';
    } else {
      el.style.background = 'rgba(255, 255, 255, 0.92)';
      el.style.border = '1.5px solid rgba(24, 24, 27, 0.75)';
      el.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.25)';
      el.style.width = '10px';
      el.style.height = '10px';
      el.style.marginLeft = '-5px';
      el.style.marginTop = '-5px';
    }
  }

  // ── Edge card ──────────────────────────────────────────────────────────
  function renderEdgeCard(id: string): void {
    const data = getPartData(id);
    const numText = data?.n ?? '·';
    const nameText = data?.name ?? `node ${id}`;
    const skuText = data?.sku ?? id;
    const priceText = data ? fmtPrice(data.price, data.currency) : '—';
    const stockColor = !data
      ? STOCK_UNK
      : data.stock === null
        ? STOCK_UNK
        : data.stock > 0
          ? STOCK_IN
          : STOCK_OUT;
    const stockText = !data || data.stock === null
      ? 'stock unknown'
      : data.stock > 0
        ? `${data.stock} in stock`
        : 'out of stock';
    edgeCard.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;">
        <div style="font-family:'Geist Mono',ui-monospace,monospace;font-size:10.5px;color:${INK_3};letter-spacing:.05em;margin-bottom:4px;">SELECTED · ${esc(numText)}</div>
        <button class="idiagram-variant-b__edge-card-close" type="button" aria-label="deselect"
          style="background:transparent;border:0;color:${INK_3};font-size:14px;line-height:1;cursor:pointer;padding:0 2px;font-family:inherit;">✕</button>
      </div>
      <div style="font-weight:600;font-size:15px;margin-bottom:2px;">${esc(nameText)}</div>
      <div style="font-family:'Geist Mono',ui-monospace,monospace;font-size:11.5px;color:${INK_2};margin-bottom:10px;">${esc(skuText)}</div>
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;">
        <span style="font-family:'Geist Mono',ui-monospace,monospace;font-size:18px;font-weight:600;">${esc(priceText)}</span>
        <span style="display:inline-flex;align-items:center;gap:6px;font-size:11.5px;color:${INK_2};">
          <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${stockColor};"></span>
          ${esc(stockText)}
        </span>
      </div>
      <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;">
        <div class="idiagram-variant-b__stepper" style="display:inline-flex;align-items:center;border:1px solid ${LINE};border-radius:6px;overflow:hidden;font-family:'Geist Mono',ui-monospace,monospace;">
          <button type="button" data-step="-1" aria-label="decrement" style="background:transparent;border:0;padding:6px 10px;cursor:pointer;font-family:inherit;font-size:14px;color:${INK_2};">−</button>
          <span class="idiagram-variant-b__qty" style="min-width:24px;text-align:center;font-size:13px;">1</span>
          <button type="button" data-step="1" aria-label="increment" style="background:transparent;border:0;padding:6px 10px;cursor:pointer;font-family:inherit;font-size:14px;color:${INK_2};">+</button>
        </div>
        <button class="idiagram-variant-b__add" type="button"
          style="background:${ACCENT};color:#fff;border:0;border-radius:6px;padding:8px 14px;font-weight:600;font-size:13px;cursor:pointer;font-family:inherit;display:inline-flex;align-items:center;gap:6px;">+ Add to cart</button>
      </div>
    `;
    edgeCard.style.display = 'block';
    // Slide-in: requestAnimationFrame so the display:block flush happens
    // before the transition runs.
    edgeCard.style.opacity = '0';
    edgeCard.style.transform = 'translateY(6px)';
    requestAnimationFrame(() => {
      edgeCard.style.opacity = '1';
      edgeCard.style.transform = 'translateY(0)';
    });
    wireEdgeCardInteraction(id, data);
  }

  function wireEdgeCardInteraction(id: string, data: PartData | null): void {
    const qtyEl = edgeCard.querySelector<HTMLElement>('.idiagram-variant-b__qty');
    const addBtn = edgeCard.querySelector<HTMLButtonElement>('.idiagram-variant-b__add');
    const closeBtn = edgeCard.querySelector<HTMLButtonElement>('.idiagram-variant-b__edge-card-close');
    if (!qtyEl || !addBtn || !closeBtn) return;
    edgeCard.querySelectorAll<HTMLButtonElement>('[data-step]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const cur = Number(qtyEl.textContent) || 1;
        const next = Math.max(1, cur + Number(btn.dataset.step ?? '0'));
        qtyEl.textContent = String(next);
      });
    });
    closeBtn.addEventListener('click', () => setActive(null));
    addBtn.addEventListener('click', () => {
      const qty = Number(qtyEl?.textContent) || 1;
      const item: CartLineItem = {
        id,
        sku: data?.sku ?? id,
        name: data?.name ?? `node ${id}`,
        qty,
        unitPrice: data?.price ?? 0,
        currency: data?.currency ?? '',
      };
      addToCart(item);
      bus.emit('cart:add', item);
    });
  }

  // ── Cart pill ──────────────────────────────────────────────────────────
  if (cartPill) {
    cartPill.addEventListener('click', (ev) => {
      if ((ev.target as HTMLElement).closest('.idiagram-variant-b__checkout')) return;
      if (cart.length === 0) return;
      bus.emit('cart:open', {});
    });
  }

  function addToCart(item: CartLineItem): void {
    const existing = cart.find((l) => l.id === item.id);
    if (existing) {
      existing.qty += item.qty;
    } else {
      cart.push({ ...item });
    }
    renderCartPill();
  }

  function renderCartPill(): void {
    if (!cartPill || !cartSummary || !checkoutBtn) return;
    if (cart.length === 0) {
      cartSummary.textContent = 'empty';
      cartSummary.style.color = INK_3;
      checkoutBtn.style.opacity = '0.45';
      checkoutBtn.style.pointerEvents = 'none';
      return;
    }
    const totalQty = cart.reduce((s, l) => s + l.qty, 0);
    const totalPrice = cart.reduce((s, l) => s + l.unitPrice * l.qty, 0);
    const currency = cart[0]?.currency ?? '';
    cartSummary.innerHTML = `
      <span style="color:${INK};font-weight:500;">${totalQty} ${totalQty === 1 ? 'part' : 'parts'}</span>
      <span style="color:${INK_3};">·</span>
      <span style="font-family:'Geist Mono',ui-monospace,monospace;color:${INK_2};">${esc(fmtPrice(totalPrice, currency))}</span>
    `;
    checkoutBtn.style.opacity = '1';
    checkoutBtn.style.pointerEvents = 'auto';
  }

  function anchorOf(id: string): import('../viewer-controller.js').Anchor | null {
    const d = dotByNodeId.get(id);
    if (!d) return null;
    return { id, x: d.lastX, y: d.lastY, visible: d.lastVisible };
  }

  // Click on the canvas background (not a dot) → deselect.
  function onContainerClick(ev: MouseEvent): void {
    const target = ev.target as HTMLElement | null;
    if (target && target.classList.contains('idiagram-variant-b__dot')) return;
    if (target && target.closest('.idiagram-variant-b__edge-card')) return;
    if (target && target.closest('.idiagram-variant-b__cart')) return;
    setActive(null);
  }
  container.addEventListener('click', onContainerClick);

  return {
    setActive,
    getCart: () => cart.map((l) => ({ ...l })),
    dispose(): void {
      unsubscribeAnchors();
      container.removeEventListener('click', onContainerClick);
      layer.remove();
      dotByNodeId.clear();
    },
  };
}

function fmtPrice(n: number, currency: string): string {
  if (!Number.isFinite(n) || n === 0) return currency ? `0.00 ${currency}` : '0.00';
  const formatted = n.toFixed(2);
  return currency ? `${formatted} ${currency}` : formatted;
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;',
  );
}
